export type NavigationItemView = 'ADMIN' | 'HOST' | 'USER' | 'TUTOR';

/** Active sidebar view: musician, host/tutor staff, or platform super admin. */
export type NavigationRoleView = 'USER' | 'STAFF' | 'SUPER_ADMIN';

export const NAVIGATION_ROLE_VIEW_STORAGE_KEY = 'bandspace_navigation_role_view';

export const NAVIGATION_ROLE_VIEW_ORDER: NavigationRoleView[] = ['USER', 'STAFF', 'SUPER_ADMIN'];

export const DEFAULT_PATH_BY_NAVIGATION_ROLE_VIEW: Record<NavigationRoleView, string> = {
  USER: '/user/search',
  STAFF: '/host/organization',
  SUPER_ADMIN: '/admin/organizations',
};

const STAFF_SECTION_DEFAULT_PATH = {
  HOST: '/host/organization',
  TUTOR: '/tutor/listing',
} as const;

export function hasStaffNavigationView(
  userRoles: string[],
  _experimentalFeaturesEnabled: boolean,
): boolean {
  return userRoles.includes('HOST') || userRoles.includes('TUTOR');
}

export function getNavItemView(item: { roles?: string[] }): NavigationItemView {
  const roles = item.roles ?? [];
  if (roles.length === 0) {
    return 'USER';
  }
  if (roles.includes('ADMIN') && roles.length === 1) {
    return 'ADMIN';
  }
  if (
    roles.includes('TUTOR') &&
    !roles.includes('HOST') &&
    !roles.includes('USER') &&
    !roles.includes('ADMIN')
  ) {
    return 'TUTOR';
  }
  if (roles.includes('HOST') && !roles.includes('USER')) {
    return 'HOST';
  }
  return 'USER';
}

export function inferNavigationRoleView(pathname: string): NavigationRoleView | null {
  if (pathname.startsWith('/admin')) {
    return 'SUPER_ADMIN';
  }
  if (pathname.startsWith('/host') || pathname.startsWith('/tutor')) {
    return 'STAFF';
  }
  if (pathname.startsWith('/user')) {
    return 'USER';
  }
  return null;
}

export function getAvailableNavigationViews(
  userRoles: string[],
  experimentalFeaturesEnabled: boolean,
): NavigationRoleView[] {
  const views: NavigationRoleView[] = [];
  if (userRoles.includes('USER')) {
    views.push('USER');
  }
  if (hasStaffNavigationView(userRoles, experimentalFeaturesEnabled)) {
    views.push('STAFF');
  }
  if (userRoles.includes('ADMIN')) {
    views.push('SUPER_ADMIN');
  }
  return views;
}

export function getDefaultNavigationPath(
  view: NavigationRoleView,
  userRoles: string[],
  _experimentalFeaturesEnabled: boolean,
): string {
  if (view === 'USER') {
    return DEFAULT_PATH_BY_NAVIGATION_ROLE_VIEW.USER;
  }
  if (view === 'SUPER_ADMIN') {
    return DEFAULT_PATH_BY_NAVIGATION_ROLE_VIEW.SUPER_ADMIN;
  }
  if (userRoles.includes('HOST')) {
    return STAFF_SECTION_DEFAULT_PATH.HOST;
  }
  if (userRoles.includes('TUTOR')) {
    return STAFF_SECTION_DEFAULT_PATH.TUTOR;
  }
  return DEFAULT_PATH_BY_NAVIGATION_ROLE_VIEW.STAFF;
}

export function readStoredNavigationRoleView(): NavigationRoleView | null {
  const stored = localStorage.getItem(NAVIGATION_ROLE_VIEW_STORAGE_KEY);
  if (stored === 'SUPER_ADMIN' || stored === 'STAFF' || stored === 'USER') {
    return stored;
  }
  // Legacy persisted views map to the combined staff view (host/tutor only).
  if (stored === 'ADMIN' || stored === 'HOST' || stored === 'TUTOR') {
    return 'STAFF';
  }
  return null;
}

export function persistNavigationRoleView(view: NavigationRoleView): void {
  localStorage.setItem(NAVIGATION_ROLE_VIEW_STORAGE_KEY, view);
}

export function resolveNavigationRoleView(options: {
  pathname: string;
  userRoles: string[];
  experimentalFeaturesEnabled: boolean;
}): NavigationRoleView {
  const availableViews = getAvailableNavigationViews(
    options.userRoles,
    options.experimentalFeaturesEnabled,
  );
  if (availableViews.length === 0) {
    return 'USER';
  }

  const inferred = inferNavigationRoleView(options.pathname);
  if (inferred && availableViews.includes(inferred)) {
    return inferred;
  }

  const stored = readStoredNavigationRoleView();
  if (stored && availableViews.includes(stored)) {
    return stored;
  }

  return availableViews[0];
}

export function filterNavigationByRoleView<T extends { roles?: string[] }>(
  items: T[],
  activeView: NavigationRoleView,
): T[] {
  if (activeView === 'SUPER_ADMIN') {
    return items.filter((item) => getNavItemView(item) === 'ADMIN');
  }
  if (activeView === 'STAFF') {
    return items.filter((item) => {
      const view = getNavItemView(item);
      return view === 'HOST' || view === 'TUTOR';
    });
  }
  return items.filter((item) => getNavItemView(item) === 'USER');
}

/**
 * Role menus use top-level `kind: 'header'` labels (User / Host / Tutor / Admin).
 * Hide those labels when only one non-empty section remains; keep them when multiple
 * sections are shown so the sidebar stays scannable.
 * Pass `alwaysHideHeaders` for org types (e.g. individual tutor) where section labels add noise.
 */
export function stripRoleMenuHeadersWhenSingleSection<T extends { kind?: string }>(
  items: T[],
  options?: { alwaysHideHeaders?: boolean },
): T[] {
  type Section = { header: T | null; items: T[] };
  const sections: Section[] = [];
  let current: Section = { header: null, items: [] };

  for (const item of items) {
    if (item.kind === 'header') {
      if (current.header !== null || current.items.length > 0) {
        sections.push(current);
      }
      current = { header: item, items: [] };
    } else {
      current.items.push(item);
    }
  }
  if (current.header !== null || current.items.length > 0) {
    sections.push(current);
  }

  const nonEmpty = sections.filter((section) => section.items.length > 0);
  const showHeaders = !options?.alwaysHideHeaders && nonEmpty.length > 1;

  return nonEmpty.flatMap((section) =>
    showHeaders && section.header ? [section.header, ...section.items] : section.items,
  );
}
